export type BuilderPropertyRecord = Record<string, unknown>;

export type ShortAnswerToolMeta = {
    criteria: (p: BuilderPropertyRecord) => boolean;
    label: string;
    defaultProperties: BuilderPropertyRecord;
};

export type ShortAnswerToolSettingsProps = {
    builderProps: BuilderPropertyRecord;
    onPatch: (patch: BuilderPropertyRecord) => void;
};

export const DEFAULT_SHORT_ANSWER_TOOL_LABEL = 'String match';

export const PLAIN_TEXT_ANSWER_TOOL_LABELS = [DEFAULT_SHORT_ANSWER_TOOL_LABEL, 'Decimal number'] as const;

export function isPlainTextAnswerTool(toolLabel: string): boolean {
    return (PLAIN_TEXT_ANSWER_TOOL_LABELS as readonly string[]).includes(toolLabel);
}

const symbolicSet: BuilderPropertyRecord = { assessment_type: 'symbolic', width: 150 };

const symbolicNumberSet: BuilderPropertyRecord = {
    ...symbolicSet,
    absolute_value: false,
    accept_E_notation: false,
    accept_scientific_notation: false,
    hide_E_notation: false,
    require_n_or_fewer_sig_figs: 0,
    require_sig_fig: false,
    require_units: false,
    tolerance: '3%',
};

export const nonSymbolicTools: ShortAnswerToolMeta[] = [
    {
        criteria: ({ tool_label, assessment_type }) =>
            tool_label === DEFAULT_SHORT_ANSWER_TOOL_LABEL || (!tool_label && (!assessment_type || assessment_type === 'string')),
        label: DEFAULT_SHORT_ANSWER_TOOL_LABEL,
        defaultProperties: {
            assessment_type: 'string',
            columns: 20,
            ignoreend: '',
            lines: 1,
            obeycase: false,
            obeylinear: false,
            obeyquote: true,
            obeyspace: false,
            preferred: false,
        },
    },
    {
        criteria: ({ tool_label, assessment_type }) => tool_label === 'Decimal number' || (!tool_label && assessment_type === 'decimal'),
        label: 'Decimal number',
        defaultProperties: {
            assessment_type: 'decimal',
            absolute_value: false,
            placeholder: 'Ex: 12',
            require_n_or_fewer_sig_figs: 0,
            require_sig_fig: false,
            tolerance: '3%',
            width: 100,
        },
    },
];

export const symbolicTools: ShortAnswerToolMeta[] = [
    {
        criteria: ({ tool_label, assessment_type, notation, require_units }) =>
            tool_label === 'Symbolic number' ||
            (!tool_label && (notation === 'symbolic number' || (assessment_type === 'symbolic' && !require_units && !notation))),
        label: 'Symbolic number',
        defaultProperties: { ...symbolicNumberSet, notation: 'symbolic number' },
    },
    {
        criteria: ({ tool_label, notation, require_units }) =>
            tool_label === 'Number and units' || (!tool_label && (notation === 'number and units' || Boolean(require_units))),
        label: 'Number and units',
        defaultProperties: {
            ...symbolicNumberSet,
            give_units: false,
            notation: 'number and units',
            physical_quantities: 'A,C,Hz,V,W',
            preferred_units: '',
            require_engineering_notation: false,
            require_strict_scientific_notation: false,
            require_units: true,
            unit_prefixes: 'G,M,k,m,μ,n,p',
            unitless_option: 'Not unitless',
        },
    },
    {
        criteria: ({ tool_label, notation }) => tool_label === 'Numeric variables' || (!tool_label && notation === 'numeric variables'),
        label: 'Numeric variables',
        defaultProperties: {
            ...symbolicSet,
            accepted_substitute_variables: '[["A_0","\\\\pi (\\\\frac{d_0}{2})^2"]]',
            correctness_methods: 'expression',
            expected_variables: '["\\\\Delta l","E","F","d_0"]',
            notation: 'numeric variables',
            tests: '[{"\\\\Delta l":1,"E":2,"F":3,"d_0":4},{"\\\\Delta l":2,"E":3,"F":4,"d_0":5}]',
            tolerance: '0',
            virtual_keyboard_layout_name: 'Kitchen sink',
        },
    },
    {
        criteria: ({ tool_label, notation }) =>
            tool_label === 'Symbolic equivalence' || (!tool_label && notation === 'symbolic equivalence'),
        label: 'Symbolic equivalence',
        defaultProperties: {
            ...symbolicSet,
            accepted_substitute_variables: '[["A_0","\\\\pi (\\\\frac{d_0}{2})^2"]]',
            expected_functions: '[]',
            expected_variables: '["\\\\Delta l","E","F","d_0"]',
            notation: 'symbolic equivalence',
            tolerance: '0',
            virtual_keyboard_layout_name: 'Kitchen sink',
        },
    },
    {
        criteria: ({ tool_label, notation }) => tool_label === 'Propositional logic' || (!tool_label && notation === 'propositional logic'),
        label: 'Propositional logic',
        defaultProperties: {
            ...symbolicSet,
            boolean_functions: 'P,Q,R',
            boolean_variables: 'p,q,r',
            correctness_methods: 'truth table,exact match ignoring parens',
            editor_atomic_symbols: JSON.stringify([
                { key: 'P', latex: 'P(x)' },
                { key: 'Q', latex: 'Q(x)' },
            ]),
            notation: 'propositional logic',
            operators: '\\land,\\lor,\\lnot,\\oplus,\\rightarrow,\\leftrightarrow',
            untyped_variables: 'x,y,z',
            support_true_and_false: false,
        },
    },
    {
        criteria: ({ tool_label, notation }) => tool_label === 'Combinatorics' || (!tool_label && notation === 'combinatorics'),
        label: 'Combinatorics',
        defaultProperties: { ...symbolicSet, notation: 'combinatorics' },
    },
    {
        criteria: ({ tool_label, notation }) => tool_label === 'Functions' || (!tool_label && notation === 'functions'),
        label: 'Functions',
        defaultProperties: {
            ...symbolicSet,
            functions_variables: 'a,b,c,d,e,f',
            correctness_methods: 'exact match',
            notation: 'functions',
        },
    },
];

export const shortAnswerToolsDefaults: ShortAnswerToolMeta[] = [...nonSymbolicTools, ...symbolicTools];
